import React, { useState, useEffect } from "react";
import { Briefcase, GraduationCap, Lock } from "lucide-react";
import apiClient from "../../utils/apiClient";
import Shimmer from "../Common/Shimmer";
import CommonToaster from "../Common/CommonToaster";
import {
  AppCard, AppCardContent, AppCardHeader, AppCardTitle, Switch,
} from "../ui";
import { usePlan } from "../../contexts/PlanContext";

/**
 * AudienceModeSettings — admin toggle that switches the institute between
 * "Student" and "Employee" terminology across the entire LMS UI (login role
 * label, sidebar group, page headers, mandatory-training audience copy).
 *
 * Functionally identical — the same models, routes, and permissions are
 * used in both modes. Only the visible labels change. Reading is gated by
 * the cbtEnterprise plan flag on the BE; if a tenant loses the plan the
 * server forces audienceMode back to 'student' on the wire.
 */

const AudienceModeSettings = () => {
  const { hasPlanFeature } = usePlan();
  const planAllows = hasPlanFeature("cbtEnterprise");

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [mode, setMode] = useState("student");
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });

  const showToast = (message, type = "success") =>
    setToast({ show: true, message, type });

  useEffect(() => {
    if (!planAllows) {
      setLoading(false);
      return;
    }
    apiClient
      .get("/institute/audience-mode")
      .then(({ data }) => setMode(data.audienceMode === "employee" ? "employee" : "student"))
      .catch(() => showToast("Failed to load audience mode", "error"))
      .finally(() => setLoading(false));
  }, [planAllows]);

  const handleToggle = async (employeeMode) => {
    setSaving(true);
    const next = employeeMode ? "employee" : "student";
    try {
      const { data } = await apiClient.patch("/institute/audience-mode", { audienceMode: next });
      setMode(data.audienceMode);
      showToast(
        data.audienceMode === "employee"
          ? "Switched to Employee terminology — refresh to see changes everywhere"
          : "Switched to Student terminology — refresh to see changes everywhere",
        "success"
      );
    } catch (err) {
      showToast(err?.response?.data?.message || "Failed to update audience mode", "error");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Shimmer height={132} count={1} />
    );
  }

  return (
    <div className="audience-mode-settings">
      <AppCard>
        <AppCardHeader>
          <AppCardTitle>
            {mode === "employee" ? <Briefcase size={18} /> : <GraduationCap size={18} />}
            Learner Terminology
          </AppCardTitle>
          <p className="settings-card-desc">
            Switch the LMS between <strong>Student</strong> and <strong>Employee</strong>{" "}
            vocabulary. Use Employee for corporate compliance training (POSH, InfoSec,
            onboarding). All data, permissions, and reports stay the same — only the
            visible labels change.
          </p>
        </AppCardHeader>
        <AppCardContent>
          {!planAllows ? (
            <div className="settings-locked-row">
              <Lock size={15} /> Available on the Enterprise plan (cbtEnterprise feature).
            </div>
          ) : (
            <>
              <Switch
                label={<><Briefcase size={15} aria-hidden="true" /> Use Employee terminology</>}
                checked={mode === "employee"}
                onChange={handleToggle}
                disabled={saving}
              />
              <p className="cms-website-settings__hint">
                {mode === "employee"
                  ? 'Login shows "Employee Login", sidebar shows "Employees", and mandatory training is framed for staff.'
                  : 'Login shows "Student Login", sidebar shows "Students", and the LMS is framed for an academic audience.'}
              </p>
            </>
          )}
        </AppCardContent>
      </AppCard>

      <CommonToaster
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast((t) => ({ ...t, show: false }))}
      />
    </div>
  );
};

export default AudienceModeSettings;
